import { TDevice } from '@cian/utils';
import {
  applyMiddleware,
  combineReducers,
  compose,
  legacy_createStore as _createStore,
  Reducer,
  Store,
  StoreEnhancer,
} from 'redux';
import { withExtraArgument } from 'redux-thunk';

import { showFeedbackReducer } from './feedback';
import { galleryReducer, IGalleryData } from './gallery';
import { createSimpleReducer, booleanReducer } from './helpers';
import { IMapState, mapReducer } from './map';
import { IMobile, mobileReducer } from './mobile';
import { INavigationLinksState, navigationLinksReducer } from './navigation';
import { DEFAULT_SUBDOMAIN } from '../constants';
import {
  TMagazineModel,
  IGenplanScheme,
  TExperimentResult,
  TUser,
  IUserGaDataLayerResponseSchema,
  TGetVillagePromotionsResponse,
  IGetVillageResponseSchema,
} from '../types';
import { IReduxThunkContext } from '../types/store';
import { TReduxActions } from '../types/store/actions';
import { isClient } from '../utils/helpers';

interface IDeviceState {
  deviceType: TDevice;
}

export interface IState {
  abGroup: number;
  subdomain: string;
  countrysideRoadRegions: number[];
  countrysideStationRegions: number[];
  villageData: IGetVillageResponseSchema;
  promotionsVillageData: TGetVillagePromotionsResponse;
  gallery: IGalleryData;
  showFeedback: boolean;
  magazine: TMagazineModel[];
  map: IMapState;
  mobile: IMobile;
  device: IDeviceState;
  navigationLinks: INavigationLinksState;
  userData?: TUser;
  userGaData?: IUserGaDataLayerResponseSchema | null;
  countrysideObjectsGalleryEnabled: boolean;
  isFatCard: boolean;
  isCroppedCard: boolean;
  buildAndSaledBlockShow: boolean;
  abUseExperiments: TExperimentResult[];
  genplan: IGenplanScheme | null;
}

export function createStore(context: IReduxThunkContext, preloadedState: IState) {
  const reducer = combineReducers({
    abGroup: createSimpleReducer(100),
    subdomain: createSimpleReducer(DEFAULT_SUBDOMAIN),
    countrysideRoadRegions: createSimpleReducer<number[]>([]),
    countrysideStationRegions: createSimpleReducer<number[]>([]),
    countrysideObjectsGalleryEnabled: booleanReducer,
    isFatCard: booleanReducer,
    isCroppedCard: booleanReducer,
    gallery: galleryReducer,
    magazine: createSimpleReducer<TMagazineModel[]>([]),
    map: mapReducer,
    mobile: mobileReducer,
    device: createSimpleReducer<IDeviceState>({ deviceType: 'desktop' }),
    navigationLinks: navigationLinksReducer,
    showFeedback: showFeedbackReducer,
    userData: createSimpleReducer<TUser>({ isAuthenticated: false } as TUser),
    userGaData: createSimpleReducer<IUserGaDataLayerResponseSchema | null>(null),
    villageData: createSimpleReducer<IGetVillageResponseSchema>({} as IGetVillageResponseSchema),
    promotionsVillageData: createSimpleReducer<TGetVillagePromotionsResponse>({} as TGetVillagePromotionsResponse),
    buildAndSaledBlockShow: booleanReducer,
    abUseExperiments: createSimpleReducer<TExperimentResult[]>([{} as TExperimentResult]),
    genplan: createSimpleReducer<IGenplanScheme | null>(null),
  }) as unknown as Reducer<IState, TReduxActions>;

  const composeEnhancers = (isClient() && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) || compose;

  return _createStore(
    reducer,
    preloadedState as IState,
    composeEnhancers(applyMiddleware(withExtraArgument(context))) as unknown as StoreEnhancer<IState>,
  ) as TReduxStore;
}

export type TReduxStore = Store<IState, TReduxActions>;
